// hooks/features/agents/useAgents.ts
import { useState, useCallback } from "react";
import useToken from "../../../hooks/features/auth/useToken";
import { Agent, AgentModulePermission } from "../../../types/features/Agent";
import { agentService } from "../../../services/agentService";

export default function useAgents() {
  const { token } = useToken();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // ============================================
  // ROUTES AGENTS
  // ============================================

  const getAgents = useCallback(async (page: number = 1, perPage: number = 20) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await agentService.getAgents(token, page, perPage);
      setAgents(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des agents");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const getAgentById = useCallback(async (id: string): Promise<Agent | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const agent = await agentService.getAgentById(token, id);
      setSelectedAgent(agent);
      return agent;
    } catch (err) {
      setError("Erreur lors du chargement de l'agent");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const updateAgent = useCallback(async (id: string, data: Partial<Agent>): Promise<Agent | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const updatedAgent = await agentService.updateAgent(token, id, data);
      setAgents((prev) => prev.map((a) => (a.id === id ? updatedAgent : a)));
      if (selectedAgent?.id === id) setSelectedAgent(updatedAgent);
      return updatedAgent;
    } catch (err) {
      setError("Erreur lors de la mise à jour de l'agent");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedAgent, token]);

  const deleteAgent = useCallback(async (id: string): Promise<boolean> => {
    if (!token) return false;
    setLoading(true);
    setError(null);
    try {
      await agentService.deleteAgent(token, id);
      setAgents((prev) => prev.filter((a) => a.id !== id));
      if (selectedAgent?.id === id) setSelectedAgent(null);
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression de l'agent");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [selectedAgent, token]);

  // ============================================
  // ROUTES PERMISSIONS DES AGENTS
  // ============================================

  const getAgentPermissions = useCallback(async (agentId: string): Promise<{
    agent: { id: string; full_name: string; email: string };
    permissions: AgentModulePermission[];
  } | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const data = await agentService.getAgentPermissions(token, agentId);
      return data;
    } catch (err) {
      setError("Erreur lors du chargement des permissions");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const assignPermissions = useCallback(async (
    agentId: string,
    permissions: { module_id: string; endpoints?: string[]; is_active?: boolean }[]
  ): Promise<boolean> => {
    if (!token) return false;
    setLoading(true);
    setError(null);
    try {
      await agentService.assignPermissions(token, agentId, permissions);
      return true;
    } catch (err) {
      setError("Erreur lors de l'attribution des permissions");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const updatePermission = useCallback(async (
    permissionId: string,
    data: { endpoints?: string[]; is_active?: boolean }
  ): Promise<boolean> => {
    if (!token) return false;
    setLoading(true);
    setError(null);
    try {
      await agentService.updatePermission(token, permissionId, data);
      return true;
    } catch (err) {
      setError("Erreur lors de la mise à jour de la permission");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const deletePermission = useCallback(async (permissionId: string): Promise<boolean> => {
    if (!token) return false;
    setLoading(true);
    setError(null);
    try {
      await agentService.deletePermission(token, permissionId);
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression de la permission");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const getAvailableModules = useCallback(async (): Promise<any[] | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const modules = await agentService.getAvailableModules(token);
      return modules;
    } catch (err) {
      setError("Erreur lors du chargement des modules disponibles");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  return {
    // Données
    agents,
    selectedAgent,
    loading,
    error,
    pagination,

    // Méthodes agents
    getAgents,
    getAgentById,
    updateAgent,
    deleteAgent,

    // Méthodes permissions
    getAgentPermissions,
    assignPermissions,
    updatePermission,
    deletePermission,
    getAvailableModules,
  };
}